import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { PenLine, ArrowRight, FileText, Loader2, Search, X } from "lucide-react";
import { fetchPosts, fetchCategories } from "../api";
import type { Post, Category } from "../types";
import { PostCard } from "../components/PostCard";

const PAGE_SIZE = 9;

function stripCategoryPrefix(name: string): string {
  if (name.startsWith("auto_")) return name.substring(5);
  return name;
}

export function Home({ articlesOnly = false }: { articlesOnly?: boolean }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [posts, setPosts] = useState<Post[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState("");

  const keyword = searchParams.get("keyword") || "";
  const currentCategory = searchParams.get("category") || "全部";
  const currentPage = Number(searchParams.get("page") || "1");

  useEffect(() => setSearchValue(keyword), [keyword]);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = searchValue.trim();
    if (articlesOnly) updateParams({ keyword: value || null, page: null });
    else navigate(value ? `/articles?keyword=${encodeURIComponent(value)}` : "/articles");
  };
  const clearSearch = () => {
    setSearchValue("");
    if (articlesOnly && keyword) updateParams({ keyword: null, page: null });
  };

  const loadPosts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchPosts({
        keyword,
        category: currentCategory,
        page: currentPage,
        pageSize: PAGE_SIZE,
      });
      setPosts(Array.isArray(data.list) ? data.list : []);
      setTotalPages(data.totalPages || 1);
    } catch {
      setPosts([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [keyword, currentCategory, currentPage]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await fetchCategories();
      setCategories(Array.isArray(data) ? data : []);
    } catch {
      setCategories([]);
    }
  }, []);

  useEffect(() => {
    if (!articlesOnly) return;
    loadPosts();
    loadCategories();
  }, [articlesOnly, loadPosts, loadCategories]);

  const updateParams = (updates: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams.toString());
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === "" || value === "全部") params.delete(key);
      else params.set(key, value);
    });
    setSearchParams(params);
  };

  return (
    <div className="overflow-hidden">
      {!articlesOnly && <section className="relative pt-28 pb-20 lg:pt-36 lg:pb-28">
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full border border-[var(--border-default)] bg-[var(--bg-surface)]/60 backdrop-blur-md text-sm text-[var(--text-secondary)] mb-10 shadow-sm animate-fade-in">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald" />
            </span>
            探索技术，沉淀知识
          </div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight leading-[1.08] mb-8 animate-fade-in animation-delay-100">
            <span className="gradient-tech">DevSpec</span>
          </h1>

          <p className="text-lg sm:text-xl text-[var(--text-secondary)] max-w-2xl mx-auto leading-relaxed mb-6 animate-fade-in animation-delay-200">
            分享技术见解，沉淀开发经验。一个为开发者打造的现代科技知识库。
          </p>

          <p className="text-sm sm:text-[15px] text-[var(--text-tertiary)] max-w-xl mx-auto mb-12 animate-fade-in animation-delay-300">
            支持 Markdown 编辑、AI 辅助写作、权限管理和付费阅读。
          </p>

          <form onSubmit={submitSearch} className="max-w-2xl mx-auto mb-8 animate-fade-in animation-delay-300">
            <div className="relative flex items-center rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)]/85 backdrop-blur-xl shadow-xl shadow-primary/10 focus-within:border-primary focus-within:shadow-primary/20 transition-all">
              <Search className="absolute left-5 w-5 h-5 text-[var(--text-tertiary)]" />
              <input value={searchValue} onChange={event => setSearchValue(event.target.value)} placeholder="搜索文章、技术方案和开发经验..." className="w-full h-14 sm:h-16 pl-14 pr-36 bg-transparent outline-none text-base text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)]" />
              {searchValue && <button type="button" onClick={clearSearch} className="absolute right-[5.75rem] w-9 h-9 inline-flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)]" aria-label="清除搜索内容"><X className="w-4 h-4" /></button>}
              <button className="absolute right-2 h-10 sm:h-12 px-5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-primary to-accent">搜索</button>
            </div>
          </form>

          <div className="flex flex-col sm:flex-row justify-center gap-4 animate-fade-in animation-delay-400">
            <button
              onClick={() => navigate("/editor")}
              className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-xl text-base font-semibold text-white bg-gradient-to-r from-primary to-accent shadow-lg shadow-primary/25 hover:shadow-xl hover:shadow-primary/30 hover:-translate-y-0.5 transition-all"
            >
              <PenLine className="w-[18px] h-[18px]" />
              开始写作
            </button>
            <button
              onClick={() => navigate("/articles")}
              className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-xl text-base font-semibold text-[var(--text-primary)] border border-[var(--border-strong)] hover:border-primary hover:text-primary-light hover:bg-primary/5 transition-all"
            >
              浏览文章
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

      </section>}

      {articlesOnly && <section className="max-w-7xl mx-auto px-6 py-12 lg:py-16">
        <form onSubmit={submitSearch} className="max-w-3xl mb-12">
          <label className="block text-sm font-semibold text-[var(--text-secondary)] mb-3">搜索文章</label>
          <div className="relative flex items-center rounded-2xl border border-[var(--border-default)] bg-[var(--bg-surface)] shadow-lg focus-within:border-primary transition-colors">
            <Search className="absolute left-5 w-5 h-5 text-[var(--text-tertiary)]" />
            <input value={searchValue} onChange={event => setSearchValue(event.target.value)} placeholder="输入关键词..." className="w-full h-14 pl-14 pr-36 bg-transparent outline-none text-[var(--text-primary)]" />
            {searchValue && <button type="button" onClick={clearSearch} className="absolute right-[5.75rem] w-9 h-9 inline-flex items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--text-primary)]" aria-label="清除搜索内容"><X className="w-4 h-4" /></button>}
            <button className="absolute right-2 h-10 px-5 rounded-xl text-sm font-semibold text-white bg-gradient-to-r from-primary to-accent">搜索</button>
          </div>
        </form>
        {/* Section header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-7">
          <div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight">最新文章</h2>
            <p className="text-[15px] text-[var(--text-tertiary)]">发现优质技术内容，与开发者共同成长</p>
          </div>
        </div>

        {/* Category filter */}
        <div className="mb-10 border-b border-[var(--border-default)] overflow-x-auto">
          <div className="flex min-w-max">
            <button
              onClick={() => updateParams({ category: null, page: null })}
              className={`px-5 py-3 border-b-2 text-sm transition-colors ${
                currentCategory === "全部"
                  ? "border-primary text-[var(--text-primary)] font-semibold"
                  : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
              }`}
            >
              全部
            </button>
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => updateParams({ category: cat.name, page: null })}
                className={`px-5 py-3 border-b-2 text-sm transition-colors ${
                  currentCategory === cat.name
                    ? "border-primary text-[var(--text-primary)] font-semibold"
                    : "border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                }`}
              >
                {stripCategoryPrefix(cat.name)}
              </button>
            ))}
          </div>
        </div>

        {/* Posts grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 text-[var(--text-tertiary)]">
            <Loader2 className="w-10 h-10 animate-spin mb-4 text-primary" />
            <p className="text-sm">加载中...</p>
          </div>
        ) : posts.length === 0 ? (
          <div className="flex flex-col items-center justify-center text-center py-24 px-6 bg-[var(--bg-surface)]/50 rounded-3xl border border-[var(--border-default)] border-dashed">
            <div className="w-24 h-24 mb-8 rounded-3xl bg-gradient-to-br from-primary/10 to-accent/10 flex items-center justify-center border border-[var(--border-default)] shadow-lg">
              <FileText className="w-10 h-10 text-[var(--text-tertiary)]" strokeWidth={1.5} />
            </div>
            <p className="text-xl font-bold text-[var(--text-primary)] mb-2">暂无文章</p>
            <p className="text-[15px] text-[var(--text-tertiary)] mb-8">成为第一个分享技术见解的人</p>
            {keyword && (
              <button
                onClick={() => updateParams({ keyword: null })}
                className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl text-sm font-semibold text-[var(--text-primary)] border border-[var(--border-strong)] hover:border-primary hover:text-primary-light hover:bg-primary/5 transition-all"
              >
                清除搜索
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid gap-7 lg:gap-9 sm:grid-cols-2 lg:grid-cols-3">
              {posts.map((post) => (
                <PostCard key={post.id} post={post} />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-20 lg:mt-24">
                <button
                  onClick={() => updateParams({ page: String(Math.max(1, currentPage - 1)) })}
                  disabled={currentPage <= 1}
                  className="px-4 py-2 rounded-lg text-sm font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:border-primary hover:text-primary-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  上一页
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    onClick={() => updateParams({ page: String(page) })}
                    className={`min-w-[40px] h-10 px-3 rounded-lg text-sm font-semibold transition-all ${
                      page === currentPage
                        ? "bg-gradient-to-r from-primary to-accent text-white shadow-md shadow-primary/25"
                        : "border border-[var(--border-default)] text-[var(--text-secondary)] hover:border-primary hover:text-primary-light"
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button
                  onClick={() => updateParams({ page: String(Math.min(totalPages, currentPage + 1)) })}
                  disabled={currentPage >= totalPages}
                  className="px-4 py-2 rounded-lg text-sm font-medium border border-[var(--border-default)] text-[var(--text-secondary)] hover:border-primary hover:text-primary-light disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </section>}
    </div>
  );
}
